import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { executionService } from '../../config/api';

// Component definitions for swimlane visualization
const COMPONENTS = [
  { id: 'EXECUTION_SERVICE', label: 'Execution Service', color: 'bg-blue-500' },
  { id: 'WORKER_SERVICE', label: 'Worker Service', color: 'bg-green-500' },
  { id: 'WORKER', label: 'Worker', color: 'bg-emerald-500' },
  { id: 'LOCK_SERVICE', label: 'Lock Service', color: 'bg-yellow-500' },
  { id: 'RECOVERY_SERVICE', label: 'Recovery Service', color: 'bg-orange-500' },
  { id: 'MARKET_DATA', label: 'Market Data', color: 'bg-purple-500' },
  { id: 'BROKER_CLIENT', label: 'Broker Client', color: 'bg-pink-500' },
  { id: 'POSITION_SERVICE', label: 'Position Service', color: 'bg-indigo-500' },
  { id: 'CONTROLLER', label: 'Controller', color: 'bg-cyan-500' },
  { id: 'SYSTEM', label: 'System', color: 'bg-gray-500' },
];

// Event type colors
const EVENT_TYPE_COLORS = {
  EXECUTION_CREATED: 'bg-blue-400',
  EXECUTION_STARTED: 'bg-green-400',
  EXECUTION_RUNNING: 'bg-green-500',
  EXECUTION_STOPPING: 'bg-yellow-400',
  EXECUTION_STOPPED: 'bg-gray-400',
  EXECUTION_COMPLETED: 'bg-blue-500',
  EXECUTION_FAILED: 'bg-red-500',
  STATUS_CHANGED: 'bg-purple-400',
  LOCK_ACQUIRED: 'bg-yellow-400',
  LOCK_RELEASED: 'bg-yellow-300',
  LOCK_FAILED: 'bg-red-400',
  WORKER_STARTED: 'bg-green-400',
  WORKER_STOPPED: 'bg-gray-400',
  WORKER_HEARTBEAT: 'bg-green-200',
  WORKER_ERROR: 'bg-red-400',
  RECOVERY_SCAN: 'bg-orange-300',
  RECOVERY_DETECTED: 'bg-orange-400',
  RECOVERY_SUCCESS: 'bg-green-400',
  RECOVERY_FAILED: 'bg-red-400',
  SIGNAL_GENERATED: 'bg-purple-400',
  ORDER_PLACED: 'bg-blue-400',
  ORDER_FILLED: 'bg-green-400',
  ORDER_REJECTED: 'bg-red-400',
  ERROR: 'bg-red-500',
  WARNING: 'bg-yellow-500',
  INFO: 'bg-blue-300',
};

// Level badge colors
const LEVEL_COLORS = {
  ERROR: 'badge-error',
  WARN: 'badge-warning',
  INFO: 'badge-info',
  DEBUG: 'badge-ghost',
};

// Format timestamp for display
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
};

// Format date for display
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

// Event marker component
const EventMarker = ({ event, onClick, isSelected }) => {
  const color = EVENT_TYPE_COLORS[event.eventType] || 'bg-gray-400';
  const levelColor = LEVEL_COLORS[event.level] || 'badge-ghost';
  
  return (
    <div
      className={`relative cursor-pointer transition-all duration-200 ${isSelected ? 'scale-125 z-10' : 'hover:scale-110'}`}
      onClick={() => onClick(event)}
      title={`${event.eventType}: ${event.message}`}
    >
      <div className={`w-3 h-3 rounded-full ${color} border-2 border-white shadow-sm`} />
      {event.level === 'ERROR' && (
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      )}
    </div>
  );
};

// Event detail panel
const EventDetailPanel = ({ event, onClose }) => {
  if (!event) return null;
  
  const levelColor = LEVEL_COLORS[event.level] || 'badge-ghost';
  
  return (
    <div className="bg-base-200 rounded-lg p-4 mb-4 relative">
      <button
        className="btn btn-ghost btn-xs absolute top-2 right-2"
        onClick={onClose}
      >
        ✕
      </button>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-base-content/60">Event Type</p>
          <p className="font-semibold">{event.eventType}</p>
        </div>
        <div>
          <p className="text-xs text-base-content/60">Component</p>
          <p className="font-semibold">{event.component}</p>
        </div>
        <div>
          <p className="text-xs text-base-content/60">Timestamp</p>
          <p className="font-mono text-sm">{new Date(event.timestamp).toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-base-content/60">Level</p>
          <span className={`badge ${levelColor} badge-sm`}>{event.level}</span>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-base-content/60">Message</p>
          <p className="text-sm">{event.message}</p>
        </div>
        {event.status && (
          <div>
            <p className="text-xs text-base-content/60">Status</p>
            <p className="font-semibold">{event.status}</p>
          </div>
        )}
        {event.previousStatus && (
          <div>
            <p className="text-xs text-base-content/60">Previous Status</p>
            <p className="text-sm">{event.previousStatus}</p>
          </div>
        )}
        {event.threadName && (
          <div>
            <p className="text-xs text-base-content/60">Thread</p>
            <p className="font-mono text-xs">{event.threadName}</p>
          </div>
        )}
        {event.errorMessage && (
          <div className="col-span-2">
            <p className="text-xs text-base-content/60">Error</p>
            <p className="text-sm text-error">{event.errorMessage}</p>
          </div>
        )}
        {event.metadata && Object.keys(event.metadata).length > 0 && (
          <div className="col-span-2">
            <p className="text-xs text-base-content/60">Metadata</p>
            <pre className="text-xs bg-base-300 p-2 rounded overflow-x-auto">
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// Swimlane row component
const SwimlaneRow = ({ component, events, timeRange, onEventClick, selectedEventId }) => {
  const componentConfig = COMPONENTS.find(c => c.id === component) || {
    id: component,
    label: component,
    color: 'bg-gray-500',
  };
  
  // Calculate position for each event based on time
  const getEventPosition = (timestamp) => {
    const eventTime = new Date(timestamp).getTime();
    const { start, end } = timeRange;
    const position = ((eventTime - start) / (end - start)) * 100;
    return Math.max(0, Math.min(100, position));
  };
  
  return (
    <div className="flex items-center border-b border-base-300 py-2">
      {/* Component label */}
      <div className="w-36 flex-shrink-0 pr-2">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${componentConfig.color}`} />
          <span className="text-xs font-medium truncate">{componentConfig.label}</span>
        </div>
      </div>
      
      {/* Timeline area */}
      <div className="flex-1 relative h-8 bg-base-200 rounded">
        {events.map((event) => (
          <div
            key={event.id}
            className="absolute top-1/2 -translate-y-1/2"
            style={{ left: `${getEventPosition(event.timestamp)}%` }}
          >
            <EventMarker
              event={event}
              onClick={onEventClick}
              isSelected={selectedEventId === event.id}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Time axis component
const TimeAxis = ({ timeRange, tickCount = 6 }) => {
  const { start, end } = timeRange;
  const duration = end - start;
  
  const ticks = useMemo(() => {
    const result = [];
    for (let i = 0; i <= tickCount; i++) {
      const time = start + (duration * i / tickCount);
      result.push({
        position: (i / tickCount) * 100,
        time: new Date(time),
      });
    }
    return result;
  }, [start, end, tickCount, duration]);
  
  return (
    <div className="flex items-center border-t border-base-300 pt-2 mt-2">
      <div className="w-36 flex-shrink-0" />
      <div className="flex-1 relative h-6">
        {ticks.map((tick, index) => (
          <div
            key={index}
            className="absolute text-xs text-base-content/60"
            style={{ left: `${tick.position}%`, transform: 'translateX(-50%)' }}
          >
            {formatTime(tick.time)}
          </div>
        ))}
      </div>
    </div>
  );
};

// Main ExecutionTimeline component
const ExecutionTimeline = ({ executionId, autoRefresh = false, refreshInterval = 5000 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterComponent, setFilterComponent] = useState(null);
  const [filterLevel, setFilterLevel] = useState(null);
  
  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await executionService.getEvents(executionId);
      setEvents(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [executionId]);
  
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);
  
  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchEvents, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchEvents]);
  
  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (filterComponent && event.component !== filterComponent) return false;
      if (filterLevel && event.level !== filterLevel) return false;
      return true;
    });
  }, [events, filterComponent, filterLevel]);
  
  // Group events by component
  const eventsByComponent = useMemo(() => {
    const grouped = {};
    filteredEvents.forEach(event => {
      const comp = event.component || 'UNKNOWN';
      if (!grouped[comp]) grouped[comp] = [];
      grouped[comp].push(event);
    });
    return grouped;
  }, [filteredEvents]);
  
  // Calculate time range
  const timeRange = useMemo(() => {
    if (filteredEvents.length === 0) {
      const now = Date.now();
      return { start: now - 60000, end: now };
    }
    
    const timestamps = filteredEvents.map(e => new Date(e.timestamp).getTime());
    const min = Math.min(...timestamps);
    const max = Math.max(...timestamps);
    const padding = (max - min) * 0.05 || 5000; // 5% padding or 5 seconds
    
    return {
      start: min - padding,
      end: max + padding,
    };
  }, [filteredEvents]);
  
  // Get unique components that have events
  const activeComponents = useMemo(() => {
    const components = new Set(filteredEvents.map(e => e.component));
    return COMPONENTS.filter(c => components.has(c.id));
  }, [filteredEvents]);
  
  if (loading && events.length === 0) {
    return (
      <div className="flex justify-center items-center h-48">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
        <button className="btn btn-sm" onClick={fetchEvents}>Retry</button>
      </div>
    );
  }
  
  return (
    <div className="bg-base-100 rounded-lg p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Execution Timeline</h3>
        <div className="flex gap-2">
          {/* Component filter */}
          <select
            className="select select-bordered select-sm"
            value={filterComponent || ''}
            onChange={(e) => setFilterComponent(e.target.value || null)}
          >
            <option value="">All Components</option>
            {COMPONENTS.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          
          {/* Level filter */}
          <select
            className="select select-bordered select-sm"
            value={filterLevel || ''}
            onChange={(e) => setFilterLevel(e.target.value || null)}
          >
            <option value="">All Levels</option>
            <option value="ERROR">Error</option>
            <option value="WARN">Warning</option>
            <option value="INFO">Info</option>
            <option value="DEBUG">Debug</option>
          </select>
          
          {/* Refresh button */}
          <button className="btn btn-ghost btn-sm" onClick={fetchEvents}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Event count */}
      <div className="text-sm text-base-content/60 mb-4">
        {filteredEvents.length} events
        {filterComponent && ` from ${filterComponent}`}
        {filterLevel && ` (${filterLevel})`}
      </div>
      
      {/* Selected event detail */}
      <EventDetailPanel
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
      
      {/* Swimlane visualization */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-8 text-base-content/60">
          No events recorded yet
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[600px]">
            {/* Date header */}
            <div className="text-xs text-base-content/60 mb-2 text-center">
              {formatDate(timeRange.start)} - {formatDate(timeRange.end)}
            </div>
            
            {/* Swimlanes */}
            {activeComponents.map(component => (
              <SwimlaneRow
                key={component.id}
                component={component.id}
                events={eventsByComponent[component.id] || []}
                timeRange={timeRange}
                onEventClick={setSelectedEvent}
                selectedEventId={selectedEvent?.id}
              />
            ))}
            
            {/* Time axis */}
            <TimeAxis timeRange={timeRange} />
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-base-300">
        <p className="text-xs text-base-content/60 mb-2">Event Types</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(EVENT_TYPE_COLORS).slice(0, 10).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${color}`} />
              <span className="text-xs">{type.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExecutionTimeline;
